/*
 * ez-video-banner — the video-tile banner EXTRAS on top of the shared ez-carousel engine.
 *
 * Transport (desktop overlay arrows / mobile dots-or-progress / snap step / scroll sync) is the
 * ONE ez-carousel.js engine on the existing `product-rail` bundle — NO new bundle was minted.
 * A multi-up peeking tile row wants exactly what the rail wants (proximity step + nearest index +
 * progress sync), and a named bundle whose config duplicates a shipped one is bundle sprawl
 * (reusable-first). This subclass adds ONLY what a video tile row needs beyond that:
 *
 *   1. THE PREVIEW GOVERNOR — the reason this file exists. Tiles ship preload="none" with their
 *      source HELD in data-ez-vb-src, so a tile that never enters the viewport costs ZERO video
 *      bytes. An IntersectionObserver tracks which tiles are actually on screen; the governor then
 *      plays EXACTLY ONE of them and holds every other tile on its poster. Tile count therefore
 *      does NOT scale concurrent decode or streaming bandwidth — the cap does. That is what makes
 *      an 8-tile row safe on a mid-range PK Android (M2 data-light rule). [2026-07-23, owner,
 *      superseding the ~1-per-2 rule from earlier the same day] the cap is a FIXED 1 on every
 *      viewport, anchored to the SECOND visible tile. `data-ez-max-playing` is no longer read and
 *      its schema setting is inert. See _cap() / _anchor() for the craft reason.
 *   2. Bail-outs that keep the data promise honest: prefers-reduced-motion OFF, Save-Data OFF,
 *      2g/slow-2g OFF, hidden tab OFF, and lightbox-open OFF (the lightbox video is the ONE video
 *      allowed to pull real bytes, so previews stand down while it runs).
 *   3. The lightbox opener wiring (a real <button> per tile -> ez-lightbox.open(key, trigger)).
 *
 * extends window.EzCarousel (ez-carousel.js is emitted FIRST; defer preserves document order).
 * Vanilla custom element, no framework, no external slider / video library (§A0 / §A5 / §A7).
 */
(function () {
  if (!window.EzCarousel || customElements.get('ez-video-banner')) return;

  var reduceMotion = window.ezCarouselReduceMotion ||
                     window.matchMedia('(prefers-reduced-motion: reduce)');

  // Never spend a metered / very slow connection on a decorative preview.
  function dataCheap() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return true;
    if (c.saveData) return false;
    if (c.effectiveType && /(^|-)2g$/.test(c.effectiveType)) return false;
    return true;
  }

  class EzVideoBanner extends window.EzCarousel {
    _afterBoot() {
      var self = this;

      this.tiles = Array.prototype.slice.call(this.querySelectorAll('.ez-vb__tile'));
      this.videos = Array.prototype.slice.call(this.querySelectorAll('[data-ez-vb-video]'));
      // NOTE: data-ez-max-playing is no longer read. The cap is a fixed 1 (see _cap), so the
      // `max_playing` schema setting is INERT — it is left in place rather than deleted because
      // removing a setting is the dangerous-category action that orphans any instance carrying a
      // value. Flagged for a gated cleanup pass; a setting that does nothing is the same dead-
      // control failure the mute button was just fixed for.
      this.visible = [];
      this._suspended = false;

      // ---- lightbox opener (a real <button> per tile; the lightbox owns the a11y contract)
      this.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-ez-vb-open]');
        if (!opener) return;
        var lb = document.getElementById(self.dataset.ezLightboxTarget || '');
        if (!lb || typeof lb.open !== 'function') return;
        e.preventDefault();
        // Pass the tile's ORDINAL alongside the key. The tile and its lightbox panel are rendered
        // by two different passes and both key off block.id; the ordinal is the lightbox's fallback
        // if those ever stop agreeing, so a key drift can never silently pin every tile to panel 1.
        var tile = opener.closest('.ez-vb__tile');
        var at = tile ? self.tiles.indexOf(tile) : -1;
        lb.open(opener.getAttribute('data-ez-vb-open'), opener, at);
      });

      // Previews stand down entirely while the lightbox runs, and resume on close.
      this._onLbOpen = function () { self._suspend(true); };
      this._onLbClose = function () { self._suspend(false); };
      document.addEventListener('ez-lightbox:open', this._onLbOpen);
      document.addEventListener('ez-lightbox:close', this._onLbClose);

      this._onVis = function () { self._suspend(document.hidden); };
      document.addEventListener('visibilitychange', this._onVis);

      this._initPreviews();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this._io) this._io.disconnect();
      document.removeEventListener('ez-lightbox:open', this._onLbOpen);
      document.removeEventListener('ez-lightbox:close', this._onLbClose);
      document.removeEventListener('visibilitychange', this._onVis);
    }

    _afterResize() { this._rebalance(); }

    /* ---- the preview governor --------------------------------------------------------- */

    _initPreviews() {
      if (this.dataset.ezPreviewAutoplay == null) return;   // merchant turned previews off
      if (reduceMotion.matches) return;                     // motion allowance -> poster only
      if (!dataCheap()) return;                             // Save-Data / 2g -> poster only
      if (!this.videos.length) return;
      if (!('IntersectionObserver' in window)) return;       // no IO -> poster only (never play-all)

      var self = this;
      this._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var v = e.target.querySelector('[data-ez-vb-video]');
          if (!v) return;
          var at = self.visible.indexOf(v);
          if (e.isIntersecting && at < 0) self.visible.push(v);
          else if (!e.isIntersecting && at >= 0) self.visible.splice(at, 1);
        });
        self._rebalance();
      }, { threshold: 0.55 });

      this.tiles.forEach(function (t) { if (t.querySelector('[data-ez-vb-video]')) self._io.observe(t); });
    }

    _suspend(on) {
      this._suspended = !!on;
      if (this._suspended) this.videos.forEach(this._hold, this);
      else this._rebalance();
    }

    // [2026-07-23 owner ruling, superseding the ~1-per-2 rule from earlier the same day] EXACTLY
    // ONE tile plays, on every viewport. Desktop previously played 2 of 4; he ruled 1.
    //
    // The reason is CRAFT, not bandwidth. M2's own conveyance: motion only POINTS when most of the
    // frame is STILL. Two moving tiles in a four-up row is half the row in motion, and the eye is
    // handed two things to follow, which is the same as being handed none — a row where everything
    // moves does not read as alive, it reads as a slot machine. One moving tile against three still
    // ones is a POINTER. This is NOT a retreat on autoplay-off: that rule governs the TRANSPORT
    // (the rail never auto-advances, never pulls content out from under a reader) and has never
    // covered a tile that stays put while its contents move.
    _cap() {
      return 1;
    }

    // WHICH tile plays: the SECOND visible one, per the owner ("keep one and i say the 2nd one").
    // In a 4-up row the 2nd cell sits near optical centre without being dead-centre, so the motion
    // reads as part of the row rather than as a spotlight on the middle of it. Falls back to the
    // first when only one tile is on screen (mobile 1-up, or either end of the scroll).
    _anchor(visibleInOrder) {
      return visibleInOrder.length >= 2 ? 1 : 0;
    }

    // Play _cap() tiles starting at the anchor. Everything else holds its poster — including
    // on-screen overflow.
    _rebalance() {
      if (!this._io || this._suspended) return;
      // IntersectionObserver fills this.visible in arrival order; "the 2nd one" means the 2nd in
      // DOM/scroll order, so rank by document position rather than by when the observer saw it.
      var vis = this.videos.filter(function (v) { return this.visible.indexOf(v) >= 0; }, this);
      var at = this._anchor(vis);
      var play = vis.slice(at, at + this._cap());
      this.videos.forEach(function (v) {
        if (play.indexOf(v) >= 0) this._run(v);
        else this._hold(v);
      }, this);
    }

    // Assign the held source on FIRST run only — nothing is fetched before this point.
    _run(v) {
      if (!v.dataset.ezVbReady) {
        var srcs = v.querySelectorAll('source[data-ez-vb-src]');
        for (var i = 0; i < srcs.length; i++) srcs[i].src = srcs[i].getAttribute('data-ez-vb-src');
        v.dataset.ezVbReady = '1';
        // data-ez-vb-on drives the CSS crossfade: the preview only becomes visible once it is
        // genuinely PLAYING, so a slow first frame never shows as a black box over the poster.
        v.addEventListener('playing', function () { v.setAttribute('data-ez-vb-on', ''); });
        v.addEventListener('pause', function () { v.removeAttribute('data-ez-vb-on'); });
        v.load();
      }
      if (!v.paused) return;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    _hold(v) {
      if (v.paused) return;
      try { v.pause(); } catch (e) {}
    }
  }

  customElements.define('ez-video-banner', EzVideoBanner);
})();
